import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const debugTraceCode = `// Engineering Pattern: Call Stack Traceability
// Using Error.stack to debug deeply nested recursions.
function processNode(node, depth = 0) {
    if (depth &gt; 50) {
        console.error('Potential Stack Overflow detected');
        console.trace(); // Dumps the current evaluation stack
        return;
    }
    // ... logic ...
    processNode(node.child, depth + 1);
}

// Logpoints: Debugging without side-effects
// In DevTools: Set a Logpoint with \`Price: \${price}, Tax: \${tax}\``;

const advancedDevToolsCode = `// Performance & Memory Profiling
// 1. Performance.mark: instrumenting high-frequency logic
performance.mark('data-sync-start');
synchronizeState();
performance.mark('data-sync-end');
performance.measure('Sync Duration', 'data-sync-start', 'data-sync-end');

// 2. Memory: Finding detached DOM nodes
let heavyRef = document.querySelector('.sidebar');
// If sidebar is removed from DOM but heavyRef persists, 
// it becomes a "Detached DOM Tree" (Memory Leak).`;

const JSDebugging = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Engineering Excellence</div>
                <h1>Advanced Debugging & Profiling</h1>
                <p className="hero-lead">
                    Move beyond `console.log`. Master the art of instrumentation,
                    Chrome DevTools Flame Graphs, and memory leak identification
                    to build high-performance, resilient web applications.
                </p>
            </div>

            <div className="content-section">
                <h2>The Call Stack & Traceability</h2>
                <p>
                    Effective debugging begins with understanding the **Call Stack**.
                    When your application throws an error, the "Stack Trace" is a
                    historical map of every function call that led to the failure.
                    In complex, recursive, or event-driven logic, using `console.trace()`
                    allows you to verify the execution path without pausing the
                    entire application thread.
                </p>
                <CodeBlock code={debugTraceCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Chrome DevTools: The Engine Room</h2>
                <p>
                    Professional engineers use the **Sources** panel to set
                    **Conditional Breakpoints**pausing execution only when a
                    specific state is met (e.g., `total &lt; 0`). This prevents
                    repetitive manual stepping through loops. Furthermore,
                    **Logpoints** allow you to inject logs into production-like
                    environments without modifying or recompiling the source code.
                </p>
            </div>

            <div className="content-section">
                <h2>Performance & Memory Profiling</h2>
                <p>
                    Visualizing execution is critical for optimization. The
                    **Performance Tab** provides a **Flame Chart**, showing exactly
                    which functions are "Long Tasks" (blocking the main thread for
                    {">"}50ms). Simultaneously, the **Memory Tab** allows for
                    **Heap Snapshots**, which are essential for identifying
                    "Detached DOM Trees"elements removed from the UI but pinned
                    in memory by stale JavaScript references.
                </p>
                <CodeBlock code={advancedDevToolsCode} language="javascript" />
            </div>

            <ProTip><p>
                    For production monitoring, rely on the native **Performance API**.
                    Methods like `performance.mark()` and `performance.measure()`
                    allow you to capture precise timing data that can be sent to
                    telemetry services. This provides real-world user metrics
                    (RUM) that browser DevTools cannot capture in isolation.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Instrumentation:** Favor `performance.mark()` for high-frequency code paths.</li>
                    <li>**Context:** Use `console.table()` for visualizing massive API response arrays.</li>
                    <li>**Isolation:** Use "Blackbox Script" in DevTools to ignore framework (React/Vue) internals during stepping.</li>
                    <li>**Resilience:** Monitor `unhandledrejection` to catch silent Promise failures.</li>
                    <li>**Leak Prevention:** Always look for growing memory graphs in the "Performance Monitor".</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSDebugging;



